import { forwardRef, useCallback, useEffect, useState } from 'react';
import { KeyboardArrowDown } from '@mui/icons-material';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import { InputMask, type InputMaskProps } from '@react-input/mask';
import { FieldHookConfig, useField } from 'formik';

import { CountryCodeSelectionDialog } from '~components/atoms';
import { FormLabel } from '~components/atoms/form';
import { CountryItem } from '~configs';
import { useModal } from '~hooks/index';
import {
  convertToNationalMask,
  formatPhoneBaseOnPattern,
  getCountry,
  getNationalNumber,
} from '~utils/index';

type Props = {
  label?: React.ReactNode;
  placeholder?: string;
  defaultHelperText?: string;
  disableCountryCode?: boolean;
  enableLongNumbers?: boolean;
  autoFormat?: boolean;
} & FieldHookConfig<string>;

const ForwardedInputMask = forwardRef<HTMLInputElement, InputMaskProps>(
  (props, forwardedRef) => {
    return (
      <Box p="8px 12px" width={1}>
        <Typography variant="body2" fontSize="small" color="text.placeholder">
          Phone Number
        </Typography>
        <InputMask ref={forwardedRef} replacement={{ '.': /\d/ }} {...props} />
      </Box>
    );
  },
);

export const PhoneField: React.FC<Props> = ({ label, ...props }) => {
  const [{ name, value, onChange, onBlur }, { touched, error }] =
    useField(props);
  const [selectedCountry, setSelectedCountry] = useState<CountryItem>(
    getCountry(value),
  );
  const [nationalNumber, setNationalNumber] = useState(
    getNationalNumber(value) || '',
  );

  const { isOpen, openModal, closeModal } = useModal();

  const handleCountryChange = useCallback(
    (country: CountryItem) => {
      setNationalNumber(
        formatPhoneBaseOnPattern(
          nationalNumber,
          convertToNationalMask(country),
        ),
      );
      setSelectedCountry(country);
      closeModal();
    },
    [closeModal, nationalNumber],
  );

  useEffect(() => {
    if (selectedCountry)
      onChange({
        target: {
          name,
          // A cleared number clears the field instead of keeping the old one
          value: nationalNumber
            ? `+${selectedCountry.dialCode} ${nationalNumber}` //Update full number into useField
            : '',
        },
      });
  }, [name, nationalNumber, onChange, selectedCountry, closeModal]);

  return (
    <>
      {label && <FormLabel label={label} />}
      <TextField
        fullWidth
        name={name}
        value={nationalNumber}
        onChange={(e) => setNationalNumber(e.target.value)}
        onBlur={onBlur}
        error={!!(touched && error)}
        helperText={touched && error}
        type="tel"
        InputProps={{
          startAdornment: (
            <Box
              display={'inline-flex'}
              alignItems={'center'}
              onClick={openModal}
              borderRight={1}
              borderColor={'lightgrey'}
              height={1}
            >
              <Typography noWrap align="center" height={'fit-content'}>
                {`${selectedCountry?.iso2.toLocaleUpperCase()} +${
                  selectedCountry?.dialCode
                }`}
              </Typography>
              <IconButton size="small">
                <KeyboardArrowDown />
              </IconButton>
            </Box>
          ),
          inputComponent: ForwardedInputMask,
          inputProps: {
            mask: convertToNationalMask(selectedCountry),
            key: convertToNationalMask(selectedCountry), //For updating value after change country
          },
        }}
        sx={{
          '& .MuiInputBase-root': {
            height: '3.5rem',
            '& .MuiFilledInput-input': { padding: 0 },
          },
        }}
      />
      {isOpen && ( // Trick to improve performance
        <CountryCodeSelectionDialog
          currentValue={selectedCountry}
          open={isOpen}
          onClose={closeModal}
          onSelected={handleCountryChange}
        />
      )}
    </>
  );
};
